/**
 * «El servidor primero, pero sin colgarse»: la otra primitiva de la cobertura
 * degradada.
 *
 * ── Por qué hay DOS y no una ───────────────────────────────────────────────
 *
 * El detalle del recorrido sirve la caché PRIMERO y refresca por detrás: es una
 * pantalla de lectura, y ahí lo que manda es que aparezca ya.
 *
 * Un asistente de edición y un modal de corrección no pueden hacer eso. Guardan
 * comparando contra los datos con los que se abrieron, así que servir la caché
 * cuando la red funciona perfectamente sería empeorar el caso normal. Lo que
 * necesitan es lo contrario: pedir al servidor, y si tarda demasiado o falla,
 * abrir con lo que haya en vez de dejar al operario mirando una rueda.
 *
 * ── La regla que no es obvia ───────────────────────────────────────────────
 *
 * 🔴 **Sin nada en caché se sigue esperando.** Un plazo, por sí solo, no crea
 * datos: a quien no tiene caché solo le convierte una espera en un error. Por
 * eso el plazo únicamente decide *cuándo dejar de esperar habiendo algo que
 * enseñar*; si no lo hay, esto se comporta exactamente como antes.
 */

/**
 * Cuánto se espera al servidor antes de abrir con lo cacheado.
 *
 * No es el techo de la cola de mutaciones (25 s): eso es lo que aguanta una
 * ESCRITURA antes de encolarse, y una escritura encolada no tiene a nadie
 * mirando la pantalla. Aquí sí lo hay, con la tablet en la mano, así que el
 * plazo es el que tarda una persona en pensar que la aplicación se ha colgado.
 */
export const CACHE_FALLBACK_DEADLINE_MS = 2000;

/*
 * 🔴 El número NO es inventado: es el que este repositorio ya usaba para esta
 * misma situación. `PRESETS_REFRESH_TIMEOUT_MS` (`gql/selectors/surveillanceRoutePresets.ts`)
 * lleva 2000 ms con esta justificación literal: «abrir el asistente es camino
 * crítico: el operario está al borde de la calzada». Aquel selector ya
 * implementaba esta misma carrera a mano; esto la generaliza, así que lo
 * coherente es compartir el plazo y no estrenar otro.
 *
 * Fuera no hay respaldo normativo que copiar, y conviene saberlo: Workbox, que
 * implementa el patrón como `NetworkFirst` + `networkTimeoutSeconds`, NO trae
 * valor por defecto (`|| 0`, o sea desactivado), y la documentación de Chrome
 * se niega explícitamente a recomendar uno — remite al percentil 75 medido en
 * campo. Medirlo contra las tablets reales queda pendiente.
 *
 * ⚠️ La semántica sí difiere de la de los presets, y a propósito: allí «no hay
 * plantillas» es un desenlace legítimo, así que al vencer el plazo se sirve la
 * caché aunque esté vacía. Aquí, sin caché, se sigue esperando.
 */

type CacheReader<T> = () => T | null | undefined | Promise<T | null | undefined>;

/**
 * Devuelve lo del servidor si llega a tiempo; si no, lo que haya en caché.
 *
 * - El servidor responde antes del plazo: se devuelve tal cual. Caso normal,
 *   idéntico a no tener esto.
 * - El servidor falla: se devuelve la caché al momento, sin esperar al plazo.
 *   Si tampoco hay caché, se propaga el error (quien llama ya sabe qué hacer).
 * - Vence el plazo: se devuelve la caché **si la hay**. Si no, se sigue
 *   esperando al servidor.
 */
export async function preferServerWithCacheFallback<T>(
  fromServer: Promise<T>,
  fromCache: CacheReader<T>,
  deadlineMs: number = CACHE_FALLBACK_DEADLINE_MS,
): Promise<T> {
  const readCache = async (): Promise<T | null> => {
    try {
      return (await fromCache()) ?? null;
    } catch {
      return null;
    }
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<'deadline'>((resolve) => {
    timer = setTimeout(() => resolve('deadline'), deadlineMs);
  });

  try {
    const outcome = await Promise.race([
      fromServer.then((value) => ({ value }), (error) => ({ error })),
      deadline,
    ]);

    if (outcome === 'deadline') {
      const cached = await readCache();
      // Sin caché no hay nada que adelantar: se sigue esperando al servidor,
      // que es exactamente lo que pasaba antes de existir esto.
      return cached !== null ? cached : await fromServer;
    }

    if ('error' in outcome) {
      const cached = await readCache();
      if (cached !== null) return cached;
      throw outcome.error;
    }

    return outcome.value;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
